import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError, resolveMedia } from "@/lib/api";
import { toast } from "sonner";
import { Header, AddBtn, Modal, In, SaveBtn, IconBtn } from "./shared";
import { PencilSimple, Trash, ArrowUp, ArrowDown, Eye, EyeSlash, UploadSimple, ArrowUpRight, Handshake } from "@phosphor-icons/react";

const empty = { name: "", website_url: "", image_url: "", enabled: true, start_date: "", end_date: "" };

export default function SponsorsTab() {
  const [sponsors, setSponsors] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/admin/sponsors").then((r) => setSponsors(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditing(null); setForm(empty); setModalOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ name: s.name || "", website_url: s.website_url || "", image_url: s.image_url || "", enabled: s.enabled !== false, start_date: s.start_date || "", end_date: s.end_date || "" }); setModalOpen(true); };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/sponsors/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("image_url", res.data.url);
      toast.success("Image uploaded");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Sponsor name is required"); return; }
    if (form.start_date && form.end_date && form.end_date < form.start_date) { toast.error("End date must be after the start date"); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/sponsors/${editing.id}`, form);
        toast.success("Sponsor updated");
      } else {
        await api.post("/sponsors", form);
        toast.success("Sponsor added");
      }
      setModalOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not save"); }
    finally { setSaving(false); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete sponsor "${s.name}"? This cannot be undone.`)) return;
    try { await api.delete(`/sponsors/${s.id}`); toast.success("Sponsor deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const toggleEnabled = async (s) => {
    try {
      await api.put(`/sponsors/${s.id}`, { name: s.name, website_url: s.website_url, image_url: s.image_url, start_date: s.start_date, end_date: s.end_date, enabled: !(s.enabled !== false) });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const move = async (index, dir) => {
    const next = index + dir;
    if (next < 0 || next >= sponsors.length) return;
    const arr = [...sponsors];
    [arr[index], arr[next]] = [arr[next], arr[index]];
    setSponsors(arr);
    try { await api.post("/sponsors/reorder", { ids: arr.map((s) => s.id) }); }
    catch (err) { toast.error("Could not reorder"); load(); }
  };

  return (
    <div data-testid="sponsors-tab">
      <Header title="Sponsors" action={<AddBtn onClick={openAdd} label="Add Sponsor" testid="add-sponsor-btn" />} />
      <p className="text-white/40 text-sm mb-6 -mt-4">Promote league sponsors on the homepage. Drag order with the arrows, toggle visibility, and set optional start/end dates. Disabled or out-of-window sponsors are hidden from the public site.</p>

      {sponsors.length === 0 ? (
        <div className="border border-dashed border-white/15 py-16 text-center" data-testid="sponsors-empty">
          <Handshake size={40} className="text-white/30 mx-auto mb-3" />
          <p className="text-white/50">No sponsors yet. Add your first sponsor to show it on the homepage.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {sponsors.map((s, i) => {
            const disabled = s.enabled === false;
            return (
              <div key={s.id} data-testid={`sponsor-row-${i}`} className={`flex items-center gap-4 border border-white/10 bg-[#121212] p-4 ${disabled ? "opacity-50" : ""}`}>
                <div className="flex flex-col">
                  <IconBtn onClick={() => move(i, -1)} testid={`sponsor-up-${i}`}><ArrowUp size={16} /></IconBtn>
                  <IconBtn onClick={() => move(i, 1)} testid={`sponsor-down-${i}`}><ArrowDown size={16} /></IconBtn>
                </div>
                <div className="w-28 h-16 bg-[#0A0A0A] border border-white/10 flex items-center justify-center shrink-0 overflow-hidden">
                  {s.image_url
                    ? <img src={resolveMedia(s.image_url)} alt={s.name} className="max-h-full max-w-full object-contain" />
                    : <span className="text-white/30 text-xs uppercase px-2 text-center">No image</span>}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-bold uppercase truncate" data-testid={`sponsor-name-${i}`}>{s.name}</p>
                  {s.website_url && <a href={s.website_url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#FF5722] hover:underline inline-flex items-center gap-1 truncate max-w-full">{s.website_url}<ArrowUpRight size={12} /></a>}
                  <p className="text-xs text-white/40 mt-0.5">{(s.start_date || s.end_date) ? `${s.start_date || "—"} → ${s.end_date || "—"}` : "Always visible"}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <IconBtn onClick={() => toggleEnabled(s)} testid={`sponsor-toggle-${i}`}>{disabled ? <EyeSlash size={18} /> : <Eye size={18} />}</IconBtn>
                  <IconBtn onClick={() => openEdit(s)} testid={`sponsor-edit-${i}`}><PencilSimple size={18} /></IconBtn>
                  <IconBtn onClick={() => remove(s)} danger testid={`sponsor-delete-${i}`}><Trash size={18} /></IconBtn>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? "Edit Sponsor" : "Add Sponsor"}>
        <form onSubmit={save} className="space-y-4" data-testid="sponsor-form">
          <In label="Business Name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Manila Grill House" data-testid="sponsor-name-input" />
          <In label="Website URL" type="url" value={form.website_url} onChange={(e) => set("website_url", e.target.value)} placeholder="https://example.com" data-testid="sponsor-url-input" />

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-white/60 mb-1.5">Logo / Banner Image</label>
            <div className="flex items-center gap-3">
              <div className="w-32 h-20 bg-[#0A0A0A] border border-white/10 flex items-center justify-center overflow-hidden shrink-0">
                {form.image_url
                  ? <img src={resolveMedia(form.image_url)} alt="preview" className="max-h-full max-w-full object-contain" data-testid="sponsor-image-preview" />
                  : <span className="text-white/30 text-xs">None</span>}
              </div>
              <input ref={fileRef} type="file" accept="image/*" onChange={uploadImage} className="hidden" data-testid="sponsor-upload-input" />
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="sponsor-upload-btn" className="flex items-center gap-2 text-xs uppercase font-bold px-3 py-2 border border-white/15 hover:border-[#FF5722] hover:text-[#FF5722] text-white/70 transition-colors disabled:opacity-50">
                <UploadSimple size={16} /> {uploading ? "Uploading…" : (form.image_url ? "Replace" : "Upload")}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-white/60 mb-1.5">Start Date (optional)</label>
              <input type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} data-testid="sponsor-start-date" className="w-full bg-[#1A1A1A] border border-white/15 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5722]" />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-white/60 mb-1.5">End Date (optional)</label>
              <input type="date" value={form.end_date} onChange={(e) => set("end_date", e.target.value)} data-testid="sponsor-end-date" className="w-full bg-[#1A1A1A] border border-white/15 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5722]" />
            </div>
          </div>

          <label className="flex items-center gap-3 cursor-pointer select-none">
            <input type="checkbox" checked={form.enabled} onChange={(e) => set("enabled", e.target.checked)} data-testid="sponsor-enabled-checkbox" className="w-5 h-5 accent-[#FF5722]" />
            <span className="text-sm text-white/75">Enabled (visible on the homepage)</span>
          </label>

          {/* Live preview */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-white/60 mb-1.5">Banner Preview</label>
            <div className="border border-white/10 bg-[#0A0A0A] flex flex-col items-center justify-center gap-2 py-6 px-4" data-testid="sponsor-preview">
              {form.image_url
                ? <img src={resolveMedia(form.image_url)} alt={form.name} className="max-h-24 w-auto max-w-full object-contain" />
                : <span className="font-display text-2xl uppercase text-white">{form.name || "Sponsor Name"}</span>}
              <span className="flex items-center gap-1 text-xs uppercase tracking-wide text-white/60">{form.name || "Sponsor Name"}{form.website_url && <ArrowUpRight size={14} />}</span>
            </div>
          </div>

          <SaveBtn loading={saving} testid="sponsor-save-btn" />
        </form>
      </Modal>
    </div>
  );
}
